import { useMemo } from 'react'

import { useTrackableValue } from '@app/packages/reactives/useTrackableValue'

import { useEvent } from '@app/hooks/useEvent'
import { useMountEffect } from '@app/hooks/useMountEffect'

import type { ResourceLoader, ResourceMerger } from './Resource'
import { Resource } from './Resource'

export function useRawResource<Data, State>(resource: Resource<Data, State>) {
  const data = useTrackableValue(resource.data)
  const loading = useTrackableValue(resource.loading)
  const error = useTrackableValue(resource.error)
  const ended = useTrackableValue(resource.ended)
  const initialLoadTime = useTrackableValue(resource.initialLoadTime)
  const lastLoadTime = useTrackableValue(resource.lastLoadTime)

  return { data, loading, error, ended, initialLoadTime, lastLoadTime, resource }
}

export function useResource<Data, State>(loader: ResourceLoader<Data, State>, merger?: ResourceMerger<Data>) {
  const l = useEvent(loader)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const m = merger ? useEvent(merger) : undefined
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resource = useMemo(() => new Resource(l, m), [])

  useMountEffect(() => {
    return () => {
      resource.abort()
    }
  })

  return useRawResource(resource)
}
